// src/api.js
//
// Thin wrapper around fetch() for the BrainAxis backend
// (https://api.brainaxis.net). It:
//   - reads REACT_APP_API_BASE
//   - attaches the current Firebase ID token to every request
//   - returns parsed JSON for apiGet/apiPost/apiPostForm/apiDelete
//   - returns a blob: object URL for fetchBlob() (used by the MRI viewer)
//   - exposes sendBeaconJSON() for fire-and-forget POSTs from unload handlers
//   - throws an ApiError with .status and .detail on non-2xx so callers can
//     branch cleanly (e.g. redirect to /login on 401)

const BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(status, detail) {
    super(detail || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

export function getApiBase() {
  return BASE;
}

function buildUrl(path) {
  if (!BASE) {
    throw new ApiError(0,
      'REACT_APP_API_BASE is not set. Add it to .env and restart npm.');
  }
  return `${BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

async function getToken(user) {
  if (!user) throw new ApiError(401, 'Not signed in.');
  try {
    return await user.getIdToken();
  } catch (err) {
    throw new ApiError(401, 'Could not obtain Firebase ID token.');
  }
}

async function parseAndCheck(res) {
  let body = null;
  try { body = await res.json(); } catch (_) { /* non-JSON */ }
  if (!res.ok) {
    const detail =
      (body && (body.detail || body.message)) ||
      res.statusText ||
      `Request failed (${res.status})`;
    throw new ApiError(res.status, detail);
  }
  return body;
}

/** GET a JSON endpoint with auth. */
export async function apiGet(path, user) {
  const token = await getToken(user);
  try {
    const res = await fetch(buildUrl(path), {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
    return await parseAndCheck(res);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, 'Network error.');
  }
}

/** POST a JSON body. user=null sends unauthenticated (for heartbeat etc.). */
export async function apiPost(path, body, user) {
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (user) headers.Authorization = `Bearer ${await getToken(user)}`;
  try {
    const res = await fetch(buildUrl(path), {
      method: 'POST', headers, body: JSON.stringify(body),
    });
    return await parseAndCheck(res);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, 'Network error.');
  }
}

/**
 * POST a FormData body (multipart/form-data) — used for file uploads.
 * Do NOT set Content-Type; the browser fills in the boundary automatically.
 *
 * `onProgress`, if provided, is called as (bytesSent, bytesTotal). To get
 * upload progress we use XMLHttpRequest (fetch doesn't expose it).
 */
export async function apiPostForm(path, formData, user, onProgress) {
  const token = await getToken(user);
  const url = buildUrl(path);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Accept', 'application/json');

    if (typeof onProgress === 'function' && xhr.upload) {
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) onProgress(ev.loaded, ev.total);
      };
    }

    xhr.onload = () => {
      let body = null;
      try { body = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch (_) { /* non-JSON */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body);
      } else {
        const detail =
          (body && (body.detail || body.message)) ||
          xhr.statusText ||
          `Upload failed (${xhr.status})`;
        reject(new ApiError(xhr.status, detail));
      }
    };

    xhr.onerror = () => reject(new ApiError(0, 'Network error during upload.'));
    xhr.onabort = () => reject(new ApiError(0, 'Upload cancelled.'));
    xhr.send(formData);
  });
}

/** DELETE a resource with auth. */
export async function apiDelete(path, user) {
  const token = await getToken(user);
  try {
    const res = await fetch(buildUrl(path), {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
    return await parseAndCheck(res);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, 'Network error.');
  }
}

/** Fire-and-forget POST using sendBeacon (no auth header — for unload handlers). */
export function sendBeaconJSON(path, body) {
  if (!BASE) return false;
  const url = buildUrl(path);
  const payload = JSON.stringify(body);
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'text/plain' });
      if (navigator.sendBeacon(url, blob)) return true;
    }
  } catch (_) { /* fall through */ }
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload, keepalive: true,
    }).catch(() => {});
    return true;
  } catch (_) {
    return false;
  }
}

/** GET a binary endpoint with auth, return a blob: object URL. */
export async function fetchBlob(path, user) {
  const token = await getToken(user);
  let res;
  try {
    res = await fetch(buildUrl(path), {
      method: 'GET', headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    throw new ApiError(0, 'Network error.');
  }
  if (!res.ok) {
    let detail = res.statusText || `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body && (body.detail || body.message)) detail = body.detail || body.message;
    } catch (_) { /* no body */ }
    throw new ApiError(res.status, detail);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
